"use client";

import DetailContext from "@/components/DetailContext";
import Link from "next/link";
import minesJSON from "@/data/mines.json";
import { slugify } from "@/utils/commonFuncs";
import MineralQuestions from "@/components/MineralQuestions";
import Gallery from "@/components/gallery";
import productsJSON from "@/data/products.json";
import { useTranslations } from "next-intl";
import ResponsiveImage from "@/components/ResponsiveImage";

const setUsageAreaListItem = (text) => {
    if (text.includes(":")) {
        const parts = text.split(":");

        return (
            <span className="text-neutral-500">
                <strong>{parts[0]}</strong>: {parts[1]}
            </span>
        );
    }

    return text;
};

export default function Product({ params: { locale, slug } }) {
    const t = useTranslations("ProductPage");

    const id = slug.split("-")[0];
    const productData = productsJSON.find((itm) => itm.id == id);

    if (!productData) {
        return null;
    }

    const facility = minesJSON.find((mine) => mine.id === productData.facilityId);
    const hasContent = (value) => Array.isArray(value) ? value.some(Boolean) : Boolean(value && value.trim());

    return (
        <main className="h-fit">
            <div className="flex flex-col min-h-screen h-fit w-full items-center pb-12">
                <div
                    className="w-full relative duration-300"
                    style={{ height: "50vh" }}
                >
                    <ResponsiveImage
                        className="w-full h-full object-cover brightness-90"
                        src={productData.image}
                        loading="eager"
                        fetchPriority="high"
                        alt={productData.name[locale]}
                    />
                    <h1 className="w-full text-4xl font-bold text-white absolute left-0 flex justify-center bottom-0 pb-5">
                        {productData.name[locale]}
                    </h1>
                </div>

                <DetailContext item={productData} locale={locale} kind="product" />
                <div className="px-3 pb-3 pt-1 w-full md:w-2/3 h-fit">
                    {productData.externalLink && <p className="mt-2 mb-3">
                        <a className="underline" href={productData.link}>
                            {locale === "tr" ? "Ürün ayrıntılarını EILE POMEX marka sitesinde inceleyin" : "Explore product details on the EILE POMEX brand website"}
                        </a>
                    </p>}
                    {facility && !productData.externalLink && <p className="mt-2 mb-3">
                        {locale === "tr" ? "İlgili işletme: " : "Related facility: "}
                        <Link className="underline" href={`/${locale}/mine/${facility.id}-${slugify(facility.name[locale])}/`}>{facility.name[locale]}</Link>
                    </p>}
                    {productData.whatIs && (
                        <div className="pt-5">
                            <h2 className="mb-1">
                                {productData.whatIs.head[locale]}
                            </h2>
                            <hr className="mb-4" />
                            <div className="text-md text-neutral-500">
                                <span>
                                    {typeof productData.whatIs.content[
                                        locale
                                    ] == "string" ? (
                                        <span>
                                            {productData.whatIs.content[locale]}
                                        </span>
                                    ) : (
                                        <ul>
                                            {productData.whatIs.content[
                                                locale
                                            ].map((itm, indx) => (
                                                <li key={indx} className="mb-2">
                                                    {setUsageAreaListItem(itm)}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="pt-5">
                        <h2 className="mb-0">{t("productInfo")}</h2>
                        <hr className="mb-4" />
                        <div className="text-md text-neutral-500">
                            {typeof productData.description[locale] ==
                            "string" ? (
                                <span>{productData.description[locale]}</span>
                            ) : (
                                <ul>
                                    {productData.description[locale].map(
                                        (itm, indx) => (
                                            <li key={indx} className="mb-2">
                                                {setUsageAreaListItem(itm)}
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    </div>

                    {productData.images.length > 0 && <div className="pt-5 mb-5">
                        <h2>{t("productImages")}</h2>
                        <Gallery images={productData.images} name={productData.name[locale]} />
                    </div>}

                    {hasContent(productData.usageAreas?.[locale]) && (
                        <div className="pt-5">
                            <h2 className="mb-1">{t("areasOfUsage")}</h2>
                            <hr className="mb-4" />
                            <div className="text-md text-neutral-500"></div>
                            {typeof productData.usageAreas[locale] ==
                            "string" ? (
                                <span>{productData.usageAreas[locale]}</span>
                            ) : (
                                <ul>
                                    {productData.usageAreas[locale].map(
                                        (itm, indx) => (
                                            <li key={indx} className="mb-3">
                                                {setUsageAreaListItem(itm)}
                                            </li>
                                        )
                                    )}
                                </ul>
                            )}
                        </div>
                    )}

                    {hasContent(productData.productVariety?.[locale]) && (
                        <div className="pt-5">
                            <h2 className="mb-1">{t("productVariety")}</h2>
                            <hr className="mb-4" />
                            <div className="text-md">
                                {productData.productVariety[locale]}
                            </div>
                        </div>
                    )}

                    {hasContent(productData.technicalInfo?.[locale]) && (
                        <div className="pt-5">
                            <h2 className="mb-1">{t("technicalInfo")}</h2>
                            <hr className="mb-4" />
                            <div className="text-md text-neutral-500">
                                {typeof productData.technicalInfo[locale] ==
                                "string" ? (
                                    <span>
                                        {productData.technicalInfo[locale]}
                                    </span>
                                ) : (
                                    <ul>
                                        {productData.technicalInfo[locale].map(
                                            (itm, indx) => (
                                                <li key={indx} className="mb-3">
                                                    {setUsageAreaListItem(itm)}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                    <MineralQuestions id={productData.id} locale={locale} />
                </div>
            </div>
        </main>
    );
}
